import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, LayoutDashboardIcon, PackageIcon, TargetIcon, MessageSquareIcon, SparklesIcon, BarChart3Icon, SettingsIcon, ShieldCheckIcon, SendIcon, CheckIcon } from 'lucide-react';
export type AccessLevel = 'none' | 'view' | 'edit' | 'full';
export interface ModuleDef {
  id: string;
  label: string;
  icon: React.ReactNode;
  caps: Record<AccessLevel, string>;
}
export const MODULES: ModuleDef[] = [{
  id: 'dashboard',
  label: 'Dashboard',
  icon: <LayoutDashboardIcon className="w-4 h-4" data-id="element-1604" />,
  caps: {
    none: 'Hidden',
    view: 'See business overview',
    edit: 'See business overview',
    full: 'See business overview'
  }
}, {
  id: 'catalogue',
  label: 'Catalogue',
  icon: <PackageIcon className="w-4 h-4" data-id="element-1605" />,
  caps: {
    none: 'No access',
    view: 'View items only',
    edit: 'Add, edit & publish items',
    full: 'Add, edit, publish & delete items'
  }
}, {
  id: 'leads',
  label: 'Leads',
  icon: <TargetIcon className="w-4 h-4" data-id="element-1606" />,
  caps: {
    none: 'No access',
    view: 'View leads & AI insights',
    edit: 'Update status, owner & notes',
    full: 'Manage, reassign & export leads'
  }
}, {
  id: 'conversations',
  label: 'Conversations',
  icon: <MessageSquareIcon className="w-4 h-4" data-id="element-1607" />,
  caps: {
    none: 'No access',
    view: 'Read conversations',
    edit: 'Read & reply to customers',
    full: 'Read, reply & manage chats'
  }
}, {
  id: 'ai',
  label: 'AI Assistant',
  icon: <SparklesIcon className="w-4 h-4" data-id="element-1608" />,
  caps: {
    none: 'No access',
    view: 'View AI knowledge',
    edit: 'Teach & edit AI rules',
    full: 'Configure all AI behavior'
  }
}, {
  id: 'analytics',
  label: 'Analytics',
  icon: <BarChart3Icon className="w-4 h-4" data-id="element-1609" />,
  caps: {
    none: 'No access',
    view: 'View reports',
    edit: 'View & export reports',
    full: 'View, export & act on insights'
  }
}, {
  id: 'settings',
  label: 'Settings',
  icon: <SettingsIcon className="w-4 h-4" data-id="element-1610" />,
  caps: {
    none: 'No access',
    view: 'View settings',
    edit: 'Edit business profile',
    full: 'Manage team, billing & profile'
  }
}];
export type AccessMap = Record<string, AccessLevel>;
export interface TeamMember {
  name: string;
  email: string;
  role: string;
  access: AccessMap;
  canViewContact: boolean;
}
const LEVELS: AccessLevel[] = ['none', 'view', 'edit', 'full'];
const LEVEL_LABEL: Record<AccessLevel, string> = {
  none: 'None',
  view: 'View',
  edit: 'Edit',
  full: 'Full'
};
export const ROLE_PRESETS: {
  id: string;
  label: string;
  desc: string;
  access: AccessMap;
}[] = [{
  id: 'Admin',
  label: 'Admin',
  desc: 'Full access to everything',
  access: {
    dashboard: 'full',
    catalogue: 'full',
    leads: 'full',
    conversations: 'full',
    ai: 'full',
    analytics: 'full',
    settings: 'full'
  }
}, {
  id: 'Manager',
  label: 'Manager',
  desc: 'Runs operations, no billing',
  access: {
    dashboard: 'view',
    catalogue: 'full',
    leads: 'full',
    conversations: 'full',
    ai: 'edit',
    analytics: 'edit',
    settings: 'view'
  }
}, {
  id: 'Sales',
  label: 'Sales',
  desc: 'Works leads & conversations',
  access: {
    dashboard: 'view',
    catalogue: 'view',
    leads: 'edit',
    conversations: 'edit',
    ai: 'view',
    analytics: 'view',
    settings: 'none'
  }
}, {
  id: 'Viewer',
  label: 'Viewer',
  desc: 'Read-only access',
  access: {
    dashboard: 'view',
    catalogue: 'view',
    leads: 'view',
    conversations: 'view',
    ai: 'view',
    analytics: 'view',
    settings: 'none'
  }
}];
interface InviteMemberModalProps {
  member?: TeamMember | null;
  onClose: () => void;
  onSave: (member: TeamMember) => void;
}
export function InviteMemberModal({
  member,
  onClose,
  onSave
}: InviteMemberModalProps) {
  const isEdit = !!member;
  const [name, setName] = useState(member?.name ?? '');
  const [email, setEmail] = useState(member?.email ?? '');
  const [role, setRole] = useState(member?.role ?? 'Sales');
  const [access, setAccess] = useState<AccessMap>(member?.access ?? ROLE_PRESETS.find(r => r.id === 'Sales')!.access);
  const [canViewContact, setCanViewContact] = useState(member?.canViewContact ?? false);
  const applyPreset = (presetId: string) => {
    const preset = ROLE_PRESETS.find(r => r.id === presetId);
    if (!preset) return;
    setRole(presetId);
    setAccess(preset.access);
  };
  const setModuleLevel = (moduleId: string, level: AccessLevel) => {
    setAccess(prev => ({
      ...prev,
      [moduleId]: level
    }));
    setRole('Custom');
  };
  const handleSave = () => {
    onSave({
      name: name || 'New Member',
      email,
      role,
      access,
      canViewContact
    });
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1611">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-1612" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-1613">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-slate-100 flex items-center justify-between" data-id="element-1614">
          <div data-id="element-1615">
            <h2 className="text-lg font-display font-bold text-dark-navy" data-id="element-1616">
              {isEdit ? `Edit access — ${member?.name}` : 'Invite team member'}
            </h2>
            <p className="text-sm text-slate-500" data-id="element-1617">
              Choose a role, then fine-tune what they can do in each module.
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1618">
            <XIcon className="w-5 h-5" data-id="element-1619" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-1620">
          {/* Identity */}
          {!isEdit && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1621">
              <div data-id="element-1622">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1623">
                  Full name
                </label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Layla Ahmed" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20" data-id="element-1624" />
              </div>
              <div data-id="element-1625">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1626">
                  Email
                </label>
                <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="name@email.com" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20" data-id="element-1627" />
              </div>
            </div>}

          {/* Role presets */}
          <div data-id="element-1628">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1629">
              Role
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-1630">
              {ROLE_PRESETS.map(p => <button key={p.id} onClick={() => applyPreset(p.id)} className={`text-left px-3 py-2.5 rounded-xl border transition-colors ${role === p.id ? 'border-primary-indigo bg-indigo-50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1631">
                  <div className={`text-sm font-bold ${role === p.id ? 'text-primary-indigo' : 'text-dark-navy'}`} data-id="element-1632">
                    {p.label}
                  </div>
                  <div className="text-[11px] text-slate-500 leading-tight" data-id="element-1633">
                    {p.desc}
                  </div>
                </button>)}
            </div>
            {role === 'Custom' && <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-ai-purple bg-purple-50 px-2.5 py-1 rounded-full" data-id="element-1634">
                <SparklesIcon className="w-3.5 h-3.5" data-id="element-1635" /> Custom permissions
              </div>}
          </div>

          {/* Permission matrix */}
          <div data-id="element-1636">
            <label className="block text-sm font-medium text-slate-700 mb-3" data-id="element-1637">
              Module access
            </label>
            <div className="border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden" data-id="element-1638">
              {MODULES.map(mod => {
              const level = access[mod.id] ?? 'none';
              return <div key={mod.id} className="p-4" data-id="element-1639">
                    <div className="flex items-center justify-between gap-3 mb-2" data-id="element-1640">
                      <div className="flex items-center gap-2.5 min-w-0" data-id="element-1641">
                        <span className="w-8 h-8 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center flex-shrink-0" data-id="element-1642">
                          {mod.icon}
                        </span>
                        <div className="min-w-0" data-id="element-1643">
                          <div className="text-sm font-medium text-dark-navy" data-id="element-1644">
                            {mod.label}
                          </div>
                          <div className="text-xs text-slate-500 truncate" data-id="element-1645">
                            {mod.caps[level]}
                          </div>
                        </div>
                      </div>
                      <div className="flex bg-slate-100 rounded-lg p-0.5 flex-shrink-0" data-id="element-1646">
                        {LEVELS.map(lv => <button key={lv} onClick={() => setModuleLevel(mod.id, lv)} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${level === lv ? 'bg-white text-primary-indigo shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1647">
                            {LEVEL_LABEL[lv]}
                          </button>)}
                      </div>
                    </div>

                    {/* Leads privacy capability */}
                    {mod.id === 'leads' && level !== 'none' && <button onClick={() => setCanViewContact(v => !v)} className="mt-2 ml-10 flex items-center gap-2 text-xs font-medium text-slate-600" data-id="element-1648">
                        <span className={`w-4 h-4 rounded border flex items-center justify-center ${canViewContact ? 'bg-primary-indigo border-primary-indigo' : 'border-slate-300'}`} data-id="element-1649">
                          {canViewContact && <CheckIcon className="w-3 h-3 text-white" data-id="element-1650" />}
                        </span>
                        <ShieldCheckIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-1651" />
                        Can view customer contact details
                      </button>}
                  </div>;
            })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-slate-100 flex justify-end gap-3" data-id="element-1652">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-50 rounded-lg" data-id="element-1653">
            Cancel
          </button>
          <button onClick={handleSave} className="px-5 py-2 bg-primary-indigo text-white text-sm font-medium rounded-lg hover:bg-indigo-700 flex items-center gap-2" data-id="element-1654">
            {isEdit ? <>
                <CheckIcon className="w-4 h-4" data-id="element-1655" /> Save Access
              </> : <>
                <SendIcon className="w-4 h-4" data-id="element-1656" /> Send Invite
              </>}
          </button>
        </div>
      </motion.div>
    </div>;
}
export function accessSummary(access: AccessMap): string {
  const active = MODULES.filter(m => (access[m.id] ?? 'none') !== 'none').length;
  return `${active} module${active === 1 ? '' : 's'}`;
}